import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import jsPDF from 'jspdf'
import type { AgreementVersion } from '@/utils/supplierAgreement'

export default function SupplierAgreementPage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [checked, setChecked] = useState(false)
  const [acceptError, setAcceptError] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['supplier-agreement', vendorId],
    queryFn: () => api.vendors.getSupplierAgreement(vendorId!),
    enabled: !!vendorId,
  })

  const agreementData = data?.data?.data

  const acceptMutation = useMutation({
    mutationFn: (version: string) => api.vendors.acceptSupplierAgreement(vendorId!, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-agreement', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', vendorId] })
      navigate('/vendor')
    },
    onError: (err: any) => {
      setAcceptError(err?.response?.data?.message || 'Failed to accept agreement. Please try again.')
    },
  })

  const [pdfError, setPdfError] = useState<string | null>(null)

  const handleDownloadPdf = () => {
    setPdfError(null)
    if (!agreementData?.agreement) {
      setPdfError('Agreement content has not finished loading. Please try again in a moment.')
      return
    }

    try {
    const agreement: AgreementVersion = agreementData.agreement
    const doc = new jsPDF({ unit: 'mm', format: 'a4' })
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 20
    const maxWidth = pageWidth - margin * 2
    let y = 20

    const addPage = () => {
      doc.addPage()
      y = 20
    }

    const checkPageBreak = (needed: number) => {
      if (y + needed > doc.internal.pageSize.getHeight() - 20) {
        addPage()
      }
    }

    const writeText = (text: string, fontSize: number, style: 'normal' | 'bold' = 'normal') => {
      doc.setFontSize(fontSize)
      doc.setFont('helvetica', style)
      const lines = doc.splitTextToSize(text, maxWidth)
      const lineHeight = fontSize * 0.5
      for (const line of lines) {
        checkPageBreak(lineHeight)
        doc.text(line, margin, y)
        y += lineHeight
      }
    }

    // Title
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text(agreement.title, pageWidth / 2, y, { align: 'center' })
    y += 8
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.text(`Version ${agreement.version} | Effective ${agreement.effectiveDate}`, pageWidth / 2, y, { align: 'center' })
    y += 10

    // SSA Sections
    for (const section of agreement.sections) {
      checkPageBreak(12)
      writeText(section.heading, 11, 'bold')
      y += 2
      writeText(section.body, 9)
      y += 4
    }

    // DPA
    y += 6
    checkPageBreak(14)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text(agreement.dpaTitle, pageWidth / 2, y, { align: 'center' })
    y += 6
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.text('Appendix to the Supplier Services Agreement', pageWidth / 2, y, { align: 'center' })
    y += 8

    for (const section of agreement.dpaSections) {
      checkPageBreak(12)
      writeText(section.heading, 11, 'bold')
      y += 2
      writeText(section.body, 9)
      y += 4
    }

    // Annex
    y += 6
    checkPageBreak(12)
    writeText(agreement.annexTitle, 11, 'bold')
    y += 2
    writeText(agreement.annexBody, 9)

    // Acceptance record on last page (only if already accepted)
    if (agreementData.accepted) {
      y += 10
      checkPageBreak(20)
      doc.setDrawColor(200)
      doc.line(margin, y, pageWidth - margin, y)
      y += 6
      writeText('Acceptance Record', 10, 'bold')
      y += 2
      writeText(`Accepted by: ${agreementData.acceptedByName}`, 9)
      writeText(`Date: ${new Date(agreementData.acceptedAt).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}`, 9)
      writeText(`Agreement version: ${agreementData.version}`, 9)
    }

    const versionLabel = agreementData.accepted ? agreementData.version : agreement.version
    doc.save(`Tixxly-Supplier-Agreement-v${versionLabel}.pdf`)
    } catch (err: any) {
      console.error('Failed to generate supplier agreement PDF', err)
      setPdfError(err?.message || 'Failed to generate PDF. Please try again or contact support.')
    }
  }

  if (isLoading) {
    return (
      <div style={{ padding: '2rem' }}>
        <h2>Supplier Agreement</h2>
        <p style={{ color: '#666' }}>Loading...</p>
      </div>
    )
  }

  if (error || !agreementData?.agreement) {
    return (
      <div style={{ padding: '2rem' }}>
        <h2>Supplier Agreement</h2>
        <p style={{ color: '#dc3545' }}>Failed to load agreement details.</p>
      </div>
    )
  }

  const agreement: AgreementVersion = agreementData.agreement
  const isAccepted: boolean = !!agreementData.accepted

  return (
    <div style={{ padding: '2rem', maxWidth: '900px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: '0 0 0.5rem 0' }}>Supplier Agreement</h2>
          {isAccepted ? (
            <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
              Accepted by <strong>{agreementData.acceptedByName}</strong> on{' '}
              <strong>
                {new Date(agreementData.acceptedAt).toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </strong>
              {' '} (Version {agreementData.version})
            </p>
          ) : (
            <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
              Please review the agreement below and accept to continue setting up your account.
            </p>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
          <button
            onClick={handleDownloadPdf}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#fff',
              color: 'var(--color-gray-700)',
              cursor: 'pointer',
              fontSize: '0.85rem',
              whiteSpace: 'nowrap',
            }}
          >
            Download as PDF
          </button>
          {pdfError && (
            <span style={{ color: '#dc2626', fontSize: '0.75rem', maxWidth: '260px', textAlign: 'right' }}>{pdfError}</span>
          )}
        </div>
      </div>

      <div style={{
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        padding: '2rem',
        backgroundColor: '#fff',
        fontSize: '0.9rem',
        lineHeight: '1.7',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h3 style={{ margin: '0 0 0.25rem 0' }}>{agreement.title}</h3>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            Version {agreement.version} | Effective {agreement.effectiveDate}
          </div>
        </div>

        {agreement.sections.map((section, i) => (
          <div key={i} style={{ marginBottom: '1.5rem' }}>
            <strong style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.95rem' }}>{section.heading}</strong>
            <div style={{ whiteSpace: 'pre-wrap' }}>{section.body}</div>
          </div>
        ))}

        <hr style={{ margin: '2rem 0', borderColor: '#dee2e6' }} />

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h3 style={{ margin: '0 0 0.25rem 0' }}>{agreement.dpaTitle}</h3>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            Appendix to the Supplier Services Agreement
          </div>
        </div>

        {agreement.dpaSections.map((section, i) => (
          <div key={`dpa-${i}`} style={{ marginBottom: '1.5rem' }}>
            <strong style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.95rem' }}>{section.heading}</strong>
            <div style={{ whiteSpace: 'pre-wrap' }}>{section.body}</div>
          </div>
        ))}

        <hr style={{ margin: '2rem 0', borderColor: '#dee2e6' }} />

        <div style={{ marginBottom: '1.5rem' }}>
          <strong style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.95rem' }}>{agreement.annexTitle}</strong>
          <div style={{ whiteSpace: 'pre-wrap' }}>{agreement.annexBody}</div>
        </div>
      </div>

      {!isAccepted && (
        <div
          style={{
            position: 'sticky',
            bottom: '1rem',
            marginTop: '2rem',
            padding: '1.25rem 1.5rem',
            border: `2px solid ${checked ? '#86efac' : '#fcd34d'}`,
            borderRadius: '10px',
            background: checked ? '#f0fdf4' : '#fffbeb',
            boxShadow: '0 10px 30px -12px rgba(15, 23, 42, 0.25)',
            zIndex: 10,
          }}
        >
          <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: checked ? '#15803d' : '#b45309', marginBottom: '0.5rem' }}>
            {checked ? 'Ready to accept' : 'Action required'}
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', fontSize: '0.95rem', lineHeight: '1.5', color: '#1f2937' }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              style={{
                marginTop: '0.2rem',
                width: '20px',
                height: '20px',
                accentColor: '#0ea5e9',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            />
            <span>
              <strong>I have read and agree</strong> to the Supplier Services Agreement (Version {agreement.version}), the Data Processing Agreement, and the Annex above on behalf of the vendor account.
            </span>
          </label>

          {acceptError && (
            <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '0.75rem 0 0' }}>{acceptError}</p>
          )}

          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8rem', color: checked ? '#15803d' : '#92400e' }}>
              {checked ? 'You can now accept and continue.' : 'Tick the box above to enable the Accept button.'}
            </span>
            <button
              disabled={!checked || acceptMutation.isPending}
              onClick={() => {
                setAcceptError(null)
                acceptMutation.mutate(agreement.version)
              }}
              style={{
                padding: '0.7rem 1.5rem',
                border: 'none',
                borderRadius: '8px',
                background: !checked || acceptMutation.isPending ? '#cbd5e1' : '#0ea5e9',
                color: '#fff',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: !checked || acceptMutation.isPending ? 'not-allowed' : 'pointer',
                boxShadow: !checked ? 'none' : '0 4px 12px -2px rgba(14, 165, 233, 0.4)',
                transition: 'background 0.15s ease, box-shadow 0.15s ease',
              }}
            >
              {acceptMutation.isPending ? 'Accepting…' : 'Accept and continue'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
